import { Toast, useToast } from '@lego/connect-components-react';
import { createContext, type ReactNode, useCallback, useContext } from 'react';
import { isAuthRedirectPending } from '@/utils/authenticated-fetch';

export type ToastType = 'success' | 'error' | 'warning';

const TOAST_KEY = 'app-toast';

const variantMap: Record<ToastType, 'success' | 'negative' | 'warning'> = {
  success: 'success',
  error: 'negative',
  warning: 'warning',
};

const ToastContext = createContext<
  | {
      showToast: (message: string, type: ToastType, autoDismiss?: boolean) => void;
      hideToast: () => void;
    }
  | undefined
>(undefined);

const ToastContextProvider = ({ children }: { children: ReactNode }) => {
  const { toast, dismissAll } = useToast();

  const showToast = useCallback(
    (message: string, type: ToastType, autoDismiss = true) => {
      if (type === 'error' && isAuthRedirectPending()) return;
      const shouldAutoClose = autoDismiss && type !== 'error';

      toast(
        <Toast variant={variantMap[type]} autoClose={shouldAutoClose} autoCloseTime={3000}>
          <Toast.Content>{message}</Toast.Content>
        </Toast>,
        { key: TOAST_KEY, replace: true }
      );
    },
    [toast]
  );

  const hideToast = useCallback(() => {
    dismissAll();
  }, [dismissAll]);

  return <ToastContext.Provider value={{ showToast, hideToast }}>{children}</ToastContext.Provider>;
};

export const ToastProvider = ({ children }: { children: ReactNode }) => (
  <>
    <Toast.Region />
    <ToastContextProvider>{children}</ToastContextProvider>
  </>
);

export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToastContext must be used within a ToastProvider');
  return context;
};
